// GigContext.jsx - Gig State Management
import React, { createContext, useContext, useState, useEffect } from 'react';
import * as gigService from '../services/gigService';
import { useAuth } from './AuthContext';

const GigContext = createContext(null);

export const useGig = () => {
  const context = useContext(GigContext);
  if (!context) {
    throw new Error('useGig must be used within GigProvider');
  }
  return context;
};

// Safe error message extractor — never returns [object Object]
const getErrMsg = (err) => err?.message || String(err) || 'Unknown error';

export const GigProvider = ({ children }) => {
  const { currentUser } = useAuth();
  
  const [liveGig, setLiveGig] = useState(null);
  const [myGigs, setMyGigs] = useState([]);
  const [nearbyGigs, setNearbyGigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ============================================
  // GIG CRUD OPERATIONS
  // ============================================

  const createGig = async (gigData) => {
    try {
      setError(null);
      if (!currentUser) throw new Error('Must be logged in to create gig');
      const result = await gigService.createGig(currentUser.uid, gigData);
      await loadMyGigs();
      return result;
    } catch (err) {
      setError(getErrMsg(err));
      throw err;
    }
  };

  const updateGig = async (gigId, updates) => {
    try {
      setError(null);
      const result = await gigService.updateGig(gigId, updates);
      if (liveGig?.id === gigId) setLiveGig({ ...liveGig, ...updates });
      await loadMyGigs();
      return result;
    } catch (err) {
      setError(getErrMsg(err));
      throw err;
    }
  };

  const deleteGig = async (gigId) => {
    try {
      setError(null);
      const result = await gigService.deleteGig(gigId);
      if (liveGig?.id === gigId) setLiveGig(null);
      await loadMyGigs();
      return result;
    } catch (err) {
      setError(getErrMsg(err));
      throw err;
    }
  };

  // ============================================
  // LOAD GIGS
  // ============================================

  const loadMyGigs = async () => {
    try {
      if (!currentUser) return;
      setLoading(true);
      const gigs = await gigService.getArtistGigs(currentUser.uid);
      setMyGigs(gigs);
      const live = gigs.find(g => g.status === 'live');
      setLiveGig(live || null);
    } catch (err) {
      // Store as string — never store the raw Error object in state
      const msg = getErrMsg(err);
      console.error('Error loading gigs:', msg);
      setError(msg);
      // Don't alert here — GigContext loads silently in background
      // The Firestore index error will resolve once the index is created
    } finally {
      setLoading(false);
    }
  };

  const loadNearbyGigs = async (userLocation) => {
    try {
      setLoading(true);
      const gigs = await gigService.getLiveGigs(userLocation, 1);
      setNearbyGigs(gigs);
    } catch (err) {
      console.error('Error loading nearby gigs:', getErrMsg(err));
      setError(getErrMsg(err));
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // GIG ACTIONS
  // ============================================

  const goLive = async (gigId) => {
    try {
      setError(null);
      await gigService.goLive(gigId);
      await loadMyGigs();
      return { success: true };
    } catch (err) {
      setError(getErrMsg(err));
      throw err;
    }
  };

  const endGig = async (gigId) => {
    try {
      setError(null);
      await gigService.endGig(gigId);
      setLiveGig(null);
      await loadMyGigs();
      return { success: true };
    } catch (err) {
      setError(getErrMsg(err));
      throw err;
    }
  };

  // ============================================
  // AUTO-LOAD GIGS ON AUTH
  // ============================================

  useEffect(() => {
    if (currentUser) {
      loadMyGigs();
    } else {
      setMyGigs([]);
      setLiveGig(null);
    }
  }, [currentUser]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    liveGig,
    myGigs,
    nearbyGigs,
    loading,
    error,        // now always a string or null, never an object
    createGig,
    updateGig,
    deleteGig,
    loadMyGigs,
    loadNearbyGigs,
    goLive,
    endGig,
  };

  return (
    <GigContext.Provider value={value}>
      {children}
    </GigContext.Provider>
  );
};
